import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Key, Mail, CheckCircle } from "lucide-react";
import { useAdminResetUserPassword } from "@/api-services/hooks/useAuth";

interface ResetUserPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | number;
  userName: string;
  userEmail: string;
  onSuccess?: () => void;
}

const ResetUserPasswordDialog: React.FC<ResetUserPasswordDialogProps> = ({
  open,
  onOpenChange,
  userId,
  userName,
  userEmail,
  onSuccess,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  // Suggested password is the user's email
  const suggestedPassword = userEmail;

  const resetPasswordMutation = useAdminResetUserPassword({
    onSuccess: () => {
      toast.success(`Password has been reset to email address for ${userName}`);
      handleClose();
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(
        error.message || `Failed to reset password for ${userName}`
      );
    },
  });

  const handleResetPassword = () => {
    resetPasswordMutation.mutate({
      userId,
      existingPassword: "", // Not needed for admin reset
      newPassword: suggestedPassword,
      confirmPassword: suggestedPassword,
    });
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(suggestedPassword).then(() => {
      setHasCopied(true);
      toast.success("Password copied to clipboard");
      setTimeout(() => setHasCopied(false), 2000);
    });
  };

  const handleClose = () => {
    setShowPassword(false);
    setHasCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Reset Password
          </DialogTitle>
          <DialogDescription>
            Reset password for <strong>{userName}</strong> ({userEmail})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Info Card */}
          <div className="bg-muted p-4 rounded-lg border">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary" />
              <div>
                <p className="font-semibold text-sm">User Email</p>
                <p className="text-sm text-muted-foreground">{userEmail}</p>
              </div>
            </div>
          </div>

          {/* Suggested Password */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Suggested Default Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={suggestedPassword}
                readOnly
                className="pr-20 bg-muted/50"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>

          {/* Copy to Clipboard Button */}
          <Button
            type="button"
            variant="outline"
            onClick={handleCopyToClipboard}
            className="w-full"
          >
            {hasCopied ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                Copied to Clipboard!
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Copy Email to Clipboard
              </>
            )}
          </Button>

          {/* Information Note */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Important:</strong> The password will be reset to the user's email address: <code className="bg-blue-100 px-2 py-1 rounded text-xs">{suggestedPassword}</code>.
              The user can then use this email address to log in and change their password.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={resetPasswordMutation.loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetPasswordMutation.loading}
              className="bg-primary hover:bg-primary/90"
            >
              {resetPasswordMutation.loading ? "Resetting..." : "Reset Password"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResetUserPasswordDialog;

